/* Build in libraries */

/* Third party libraries */

/* Components */
import PieChart from 'components/UI/Charts/PieChart';
import Card from 'components/UI/Card';
import ApprovedApplicationsWidget from 'components/Rover/Widgets/ApprovedApplicationsWidget';
import BreakdownByScopeWidget from 'components/Rover/Widgets/BreakdownByScopeWidget';
import TotalApplicationsWidget from 'components/Rover/Widgets/TotalApplicationsWidget';

/* Services */
import { gender, races } from '../CRForm/ApplicationInformation/constants';
import { convertIntoPercentage } from './helpers';

interface IApplicationDashboardComponentProps {
  data: any;
  children: JSX.Element;
}

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const CRApprovedDashboard = ({
  data,
  children,
}: IApplicationDashboardComponentProps) => {
  const applicationsList = data?.data;

  const widgetsData: {
    totalApplications: number;
    totalApprovedApplications: number;
    totalHousePrice: number;
    statusSummary: { [key: string]: number };
    gender: IPieChart;
    totalToBeInspected: number;
    totalReceivedAssistance: number;
    maritalStatus: IPieChart;
    totalRaces: IPieChart;
    totalDisabled: IPieChart;
    totalFemaleHeadOfHouseHold: IPieChart;
    totalIncomeBreakDown: IPieChart;
    totalHouseBuiltBefore1978: IPieChart;
    applicationLocationList: Array<{
      id: string;
      [key: string]: string;
      fullAddress: string;
    }>;
    breakdownByScope: { [key: string]: number };
  } = {
    totalApplications: 0,
    totalApprovedApplications: 0,
    totalHousePrice: 0,
    statusSummary: {},
    totalRaces: {
      label: races,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#2B6CB0' },
        { data: 0, color: '#F27141' },
      ],
    },
    totalDisabled: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalFemaleHeadOfHouseHold: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalIncomeBreakDown: {
      label: gender,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalHouseBuiltBefore1978: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    gender: {
      label: ['Female', 'Male'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalToBeInspected: 0,
    totalReceivedAssistance: 0,
    maritalStatus: {
      label: ['Married', 'Unmarried', 'Widowed'],
      dataSets: [
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#2B6CB0' },
      ],
    },
    breakdownByScope: {},
    applicationLocationList: [],
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData } = a;

    //Total Application Widget
    widgetsData['totalApplications'] += 1;

    if (a.status === 'APPROVED') {
      //Total Application Widget
      widgetsData['totalApprovedApplications'] += 1;

      // Total Amount Requested
      widgetsData['totalHousePrice'] += applicationData?.propertyInformation
        ?.housePrice
        ? applicationData?.propertyInformation?.housePrice
        : 0;

      //Status Summary
      widgetsData['statusSummary'][a.status] =
        (widgetsData['statusSummary'][a.status] ?? 0) + 1;

      // Total Races
      applicationData?.application?.race.forEach((r: any) => {
        if (r === 'African-American')
          widgetsData.totalRaces.dataSets[0].data += 1;
        if (r === 'Caucasian') widgetsData.totalRaces.dataSets[1].data += 1;
        if (r === 'Hispanic') widgetsData.totalRaces.dataSets[2].data += 1;
        if (r === 'Other') widgetsData.totalRaces.dataSets[3].data += 1;
      });

      // Total Disabled
      if (applicationData?.application?.disabled === 'yes') {
        widgetsData.totalDisabled.dataSets[0].data += 1;
      }
      if (applicationData?.application?.disabled === 'no') {
        widgetsData.totalDisabled.dataSets[1].data += 1;
      }

      // Total Female Head of Household
      if (applicationData?.application?.femaleHeadOfHousehold === 'yes') {
        widgetsData.totalFemaleHeadOfHouseHold.dataSets[0].data += 1;
      }
      if (applicationData?.application?.femaleHeadOfHousehold === 'no') {
        widgetsData.totalFemaleHeadOfHouseHold.dataSets[1].data += 1;
      }

      // Total House Built Before 1978
      if (applicationData?.application?.wasBuiltBefore1978 === 'yes') {
        widgetsData.totalHouseBuiltBefore1978.dataSets[0].data += 1;
      }
      if (applicationData?.application?.wasBuiltBefore1978 === 'no') {
        widgetsData.totalHouseBuiltBefore1978.dataSets[1].data += 1;
      }

      // gender
      if (applicationData?.application?.gender === 'Female') {
        widgetsData.gender.dataSets[0].data += 1;
      }
      if (applicationData?.application?.gender === 'Male') {
        widgetsData.gender.dataSets[1].data += 1;
      }

      // marital status
      if (applicationData?.application?.maritalStatus === 'Married') {
        widgetsData.maritalStatus.dataSets[0].data += 1;
      }
      if (applicationData?.application?.maritalStatus === 'Un-Married') {
        widgetsData.maritalStatus.dataSets[1].data += 1;
      }
      if (applicationData?.application?.maritalStatus === 'Widow') {
        widgetsData.maritalStatus.dataSets[2].data += 1;
      }

      // Total To Be Inspected
      if (a.status === 'PHYSICAL_INSPECTION') {
        widgetsData.totalToBeInspected += 1;
      }

      // Total Denied - 3 Year Rule
      if (applicationData?.application?.receivedAssistance === 'yes')
        widgetsData.totalReceivedAssistance += 1;

      //Breakdown By Scope
      const { scope } = applicationData;
      for (const key in scope) {
        if (scope[key]) {
          widgetsData['breakdownByScope'][key] =
            (widgetsData['breakdownByScope'][key] ?? 0) + 1;
        }
      }
    }
  });

  const transformObject = (data: { [key: string]: number }) => {
    return Object.entries(data).map(([scope, projects]) => ({
      scope,
      projects,
    }));
  };

  return (
    <>
      <>
        <div className='my-4'>
          <section className='my-4 grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
            <TotalApplicationsWidget
              count={widgetsData?.totalApplications ?? 0}
            />
            <ApprovedApplicationsWidget
              count={widgetsData?.totalApprovedApplications ?? 0}
            />
          </section>
          <div className='my-4'>{children}</div>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
            <Card title='Marital Status'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-5/5 h-80'>
                  <PieChart
                    labels={widgetsData?.maritalStatus?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.maritalStatus?.dataSets
                    )}
                    chartTitle=''
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
            <Card title='Gender'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-5/5 h-80'>
                  <PieChart
                    labels={widgetsData?.gender?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.gender?.dataSets
                    )}
                    chartTitle=''
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Race'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-5/5 h-80'>
                  <PieChart
                    labels={widgetsData?.totalRaces?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.totalRaces?.dataSets
                    )}
                    chartTitle=''
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
            <Card title='Disabled'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-5/5 h-80'>
                  <PieChart
                    labels={widgetsData?.totalDisabled?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.totalDisabled?.dataSets
                    )}
                    chartTitle=''
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Income Breakdown'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-5/5 h-80'>
                  <PieChart
                    labels={widgetsData?.totalRaces?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.totalRaces?.dataSets
                    )}
                    chartTitle=''
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
            <Card title='House Built Before 1978'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-5/5 h-80'>
                  <PieChart
                    labels={widgetsData?.totalHouseBuiltBefore1978?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.totalHouseBuiltBefore1978?.dataSets
                    )}
                    chartTitle=''
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
            <Card title='Female Head of Household'>
              <div className='p-5 flex flex-col items-center '>
                <div className='w-5/6 lg:w-5/5 h-80'>
                  <PieChart
                    labels={widgetsData?.totalFemaleHeadOfHouseHold?.label}
                    datasets={convertIntoPercentage(
                      widgetsData?.totalFemaleHeadOfHouseHold?.dataSets
                    )}
                    chartTitle=''
                    showValue={true}
                  />
                </div>
              </div>
            </Card>
          </section>
          <section className='grid md:grid-cols-2 xl:grid-cols-2 gap-4 mt-4'>
            <BreakdownByScopeWidget
              breakdownByScope={transformObject(widgetsData.breakdownByScope)}
            />
          </section>
        </div>
      </>
    </>
  );
};
